/**
 * Works out which article documents the page the user is currently on.
 *
 * Two sources, in order:
 *
 * 1. An explicit `usePageHelp('…')` declaration. Needed where one route has several
 *    documented surfaces — the travel file's four tabs share a route, so only the page
 *    itself can say which tab is showing.
 * 2. Otherwise the article whose `route` best matches the current path.
 *
 * The second is why 90-odd pages get contextual help without 90-odd edits. It is not the
 * central route→article map that `HelpContext` warns against: the mapping lives in each
 * article's own frontmatter, next to the content it describes, so an article and its
 * route move together. A route that changes without its article being updated shows up
 * as a page with no contextual help, not as a wrong article.
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import generated from '../generated/help-index.json';
import { SOURCE_LOCALE, type HelpIndex } from './types';

const index = generated as unknown as HelpIndex;

/**
 * Longest matching route wins, so a specific page beats its section's landing page.
 *
 * Exported as a plain function, and the declared id is passed in rather than read from
 * context, so this module does not import `HelpContext` — which imports the overlay,
 * which would import this. No cycle to reason about.
 */
export const matchByRoute = (pathname: string): string | null => {
    let best: { id: string; length: number } | null = null;

    for (const article of index.articles) {
        if (article.locale !== SOURCE_LOCALE || !article.route || article.tab) continue;
        // Exact, or the article's route is a prefix of where we are (a detail page under
        // its list). Guard the boundary so `/finance/payment-book` does not match
        // `/finance/payment-book-archive`.
        const isPrefix = pathname === article.route || pathname.startsWith(`${article.route}/`);
        if (!isPrefix) continue;
        if (!best || article.route.length > best.length) {
            best = { id: article.id, length: article.route.length };
        }
    }

    return best?.id ?? null;
};

/** `declared` is a page's explicit `usePageHelp` id, when it has one. */
export const useContextualArticleId = (declared: string | null): string | null => {
    const location = useLocation();
    return useMemo(() => declared ?? matchByRoute(location.pathname), [declared, location.pathname]);
};
